import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useReducer, useCallback } from "react";

import { rebuildEmailHtml } from "../utils";
import {
  FrameEditorContext,
  type FrameEditorContextValue,
} from "./FrameEditorContext";
import type { Frame, FrameVariant } from "@/types/dragon-tiles";
import type { Editor } from "@grapesjs/studio-sdk-plugins/dist/types.js";
import type { ReactNode } from "react";

import {
  emailPreviewKeys,
  frameQueryOptions,
  useUpdateFrame,
} from "@/api/dragon-tiles";
import {
  frameReplaceFields,
  narrowVariantToUpdate,
  updateFramePayload,
  updateFrameVariantById,
} from "@/frames/service";

export interface FrameEditorState {
  editor: Editor | null;
  frame: Frame;
  selectedVariant: FrameVariant;
}

export type FrameEditorAction =
  | { type: "SET_EDITOR"; payload: Editor }
  | { type: "SET_FRAME"; payload: Frame }
  | { type: "UPDATE_FRAME"; payload: Partial<Frame> }
  | { type: "SET_SELECTED_VARIANT"; payload: FrameVariant }
  | {
      type: "INITIALIZE";
      payload: {
        frame: Frame;
        selectedVariant: FrameVariant;
      };
    };

// Reducer function
function frameEditorReducer(
  state: FrameEditorState,
  action: FrameEditorAction,
): FrameEditorState {
  switch (action.type) {
    case "SET_EDITOR":
      return { ...state, editor: action.payload };
    case "SET_FRAME":
      return { ...state, frame: action.payload };
    case "UPDATE_FRAME": {
      return { ...state, frame: { ...state.frame, ...action.payload } };
    }
    case "SET_SELECTED_VARIANT":
      return { ...state, selectedVariant: action.payload };
    case "INITIALIZE":
      return {
        ...state,
        frame: action.payload.frame,
        selectedVariant: action.payload.selectedVariant,
      };
    default:
      return state;
  }
}

interface FrameEditorProviderProps {
  children: ReactNode;
  initialFrame: Frame;
  siteId: string;
  onSave?: (frame: Frame, updatedVariants: FrameVariant[]) => Frame;
  onSaveWithFrame?: (frame: Frame) => Frame;
}

export function FrameEditorProvider({
  children,
  initialFrame,
  siteId,
  onSave,
  onSaveWithFrame,
}: FrameEditorProviderProps) {
  const [state, dispatch] = useReducer(frameEditorReducer, {
    editor: null,
    frame: initialFrame,
    selectedVariant:
      initialFrame.frame_variants.find((v) => v.medium === "email") ??
      initialFrame.frame_variants[0],
  });

  const { mutateAsync: updateFrame, isPending } = useUpdateFrame();
  const queryClient = useQueryClient();
  const router = useRouter();

  const handleSave = async () => {
    try {
      if (!state.editor) throw new Error("Editor not initialized");
      if (!state.frame) throw new Error("Tile not found");
      if (!state.selectedVariant) throw new Error("No variant selected");

      const originalContentText = state.selectedVariant.content_text || "";
      const grapesHTML = state.editor.getHtml() || "";

      let updatedHTML = "";
      let updatedJSON = {};

      const contentJson = state.selectedVariant.content_json;
      const isMJML =
        contentJson !== null &&
        typeof contentJson === "object" &&
        !("authoringState" in contentJson) &&
        (contentJson as { custom?: { projectType?: string } }).custom
          ?.projectType === "email";

      if (isMJML) {
        const files = (await state.editor.runCommand(
          "studio:projectFiles",
        )) as Array<{ mimeType: string; content: string }>;
        const html = files.find(
          (file: { mimeType: string; content: string }) =>
            file.mimeType === "text/html",
        )?.content;

        updatedHTML = html || "";
        updatedJSON = state.editor.getProjectData();
      } else {
        updatedHTML = rebuildEmailHtml(originalContentText, grapesHTML);
        updatedJSON = {};
      }

      // Update the content for the selected variant, keep the rest unchanged
      const updatedVariants = state.frame.frame_variants.map((variant) =>
        variant.id === state.selectedVariant.id
          ? { ...variant, content_text: updatedHTML, content_json: updatedJSON }
          : variant,
      );

      let resultFrame: Frame;

      if (onSave) {
        // Use custom save logic
        resultFrame = onSave(state.frame, updatedVariants);
      } else {
        // state.frame is a mount-time snapshot; its metadata goes stale when
        // tags/transactional are saved out-of-band. Echo the fresh copy so the
        // PUT doesn't clobber it (FOG-2267).
        const fresh = await queryClient.fetchQuery(
          frameQueryOptions(siteId, state.frame.id),
        );
        const payload = updateFrameVariantById({
          frame: state.frame,
          variantId: state.selectedVariant.id,
          contentText: updatedHTML,
          contentJson: updatedJSON,
          frameFields: { metadata: fresh.metadata },
        });

        resultFrame = await updateFrame({
          siteId,
          frameId: state.frame.id,
          payload,
        });
      }

      // Update state with the result
      dispatch({ type: "SET_FRAME", payload: resultFrame });
      const newVariant =
        resultFrame.frame_variants.find(
          (v) => v.id === state.selectedVariant.id,
        ) || resultFrame.frame_variants[0];
      dispatch({ type: "SET_SELECTED_VARIANT", payload: newVariant });

      // Invalidate queries
      void queryClient.invalidateQueries({
        queryKey: ["frame", siteId, state.frame.id],
      });
      void queryClient.invalidateQueries({
        queryKey: emailPreviewKeys.frame(siteId, state.frame.id),
      });
      void router.invalidate();
    } catch (error) {
      console.error("Save operation failed:", error);
      throw error;
    }
  };

  const handleSaveWithFrame = useCallback(
    async (frameToSave: Frame) => {
      try {
        if (!frameToSave) throw new Error("Tile not found");

        let resultFrame: Frame;
        if (onSaveWithFrame) {
          resultFrame = onSaveWithFrame(frameToSave);
        } else {
          // frameToSave carries stale metadata (mount-time snapshot); the PUT
          // reconciles the whole frame, so echo the fresh metadata to avoid
          // wiping tags/transactional saved out-of-band (FOG-2267).
          const fresh = await queryClient.fetchQuery(
            frameQueryOptions(siteId, frameToSave.id),
          );
          const payload = updateFramePayload({
            ...frameReplaceFields(frameToSave),
            metadata: fresh.metadata,
            frame_variants: frameToSave.frame_variants.map(
              narrowVariantToUpdate,
            ),
          });

          resultFrame = await updateFrame({
            siteId,
            frameId: frameToSave.id,
            payload,
          });
        }

        // Update state with the server result and keep selected variant in sync
        dispatch({ type: "SET_FRAME", payload: resultFrame });

        const newSelected =
          resultFrame.frame_variants.find(
            (v) => v.id === state.selectedVariant.id,
          ) ?? resultFrame.frame_variants[0];

        dispatch({ type: "SET_SELECTED_VARIANT", payload: newSelected });
        return resultFrame;
      } catch (error) {
        console.error("Save (with-tile) failed:", error);
        throw error;
      }
    },
    [onSaveWithFrame, updateFrame, siteId, state.selectedVariant.id],
  );

  const value: FrameEditorContextValue = {
    state,
    dispatch,
    handleSave,
    handleSaveWithFrame,
    isPending,
    siteId,
  };

  return (
    <FrameEditorContext.Provider value={value}>
      {children}
    </FrameEditorContext.Provider>
  );
}
